import React, { useRef, useState } from 'react';
import { AlertCircle, AlertTriangle, Check, Loader2, Percent, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import NumberInput, {
  type NumberInputRef,
} from '@/components/ui/NumberInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ListaReordenable } from '@/components/ui/ListaReordenable';
import type {
  BasesCalculo,
  Costo,
  CostoFormData,
  TipoBonificacion,
  TipoItem,
  TipoMoneda,
} from '@/types/costo';
import type { DestinoPagoOpcion } from '@/presupuestos/types';
import type { ModoEdicionInline } from './config';
import { SelectorDestinoPago, TEXTO_SIN_DESTINO } from './SelectorDestinoPago';
import {
  armarBonificacion,
  BASES_CALCULO_VACIAS,
  describirBonificacion,
  describirCalculo,
  faltaCotizacion,
} from './calculo';
import { MonedaImpresion } from './MonedaImpresion';

interface ListadoCostosProps {
  costos: Costo[];
  onActualizarCosto: (
    costoId: string,
    data: Partial<CostoFormData>
  ) => Promise<unknown>;
  onEliminarCosto: (costoId: string) => void;
  isLoading?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  /** Ver `MODO_EDICION_COSTOS` en `./config`. */
  modoEdicion?: ModoEdicionInline;
  /**
   * Guarda el orden de impresión (ids en el orden nuevo). Sin este callback, o sin
   * `canEdit`, no se ofrece reordenar.
   */
  onReordenar?: (ids: string[]) => Promise<unknown>;
  /** Mensaje cuando la caja está vacía. */
  mensajeVacio?: string;
  /**
   * Abre la edición del cálculo sobre la base imponible de un ítem (solo
   * presupuestos proforma). Sin este callback no se ofrece la acción.
   */
  onEditarCalculo?: (costo: Costo) => void;
  /** Bases del presupuesto sobre las que se calculan los ítems porcentuales. */
  bases?: BasesCalculo;
  /**
   * Cotización del dólar: sin ella, un ítem en otra moneda que su base se marca,
   * y uno que se imprime en otra moneda sale en la suya.
   */
  cotizacionDolar?: number | null;
  /** Destinos de pago para elegir en cada ítem; sin ellos no se muestra el selector. */
  destinosPago?: DestinoPagoOpcion[];
}

const formatearMonto = (monto: number, moneda: string): string => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: moneda,
    minimumFractionDigits: 2,
  }).format(monto);
};

type CampoEditable = 'nombre' | 'monto' | 'importeReal' | 'bonificacion' | 'tipo' | 'moneda';

interface EdicionActiva {
  costoId: string;
  campos: CampoEditable[];
  valores: {
    nombre: string;
    monto: string;
    importeReal: string;
    bonificacionTipo: TipoBonificacion;
    /** Vacío = sin bonificación. */
    bonificacionValor: string;
    tipo: TipoItem;
    moneda: TipoMoneda;
  };
}

const MONTO_MAXIMO = 999999999999.99;

const valorImporteReal = (costo: Costo): string =>
  costo.importeReal === null || costo.importeReal === undefined
    ? ''
    : String(costo.importeReal);

/** Monto antes de la bonificación: es el que se edita inline. */
const brutoDe = (costo: Costo): number => costo.montoBruto ?? costo.monto;

const valoresIniciales = (costo: Costo): EdicionActiva['valores'] => ({
  nombre: costo.nombre,
  monto: String(brutoDe(costo)),
  importeReal: valorImporteReal(costo),
  bonificacionTipo: costo.bonificacionTipo ?? 'porcentaje',
  bonificacionValor:
    costo.bonificacionValor === null || costo.bonificacionValor === undefined
      ? ''
      : String(costo.bonificacionValor),
  tipo: costo.tipo,
  moneda: costo.moneda,
});

const tieneHastaDosDecimales = (monto: number): boolean =>
  Math.abs(monto * 100 - Math.round(monto * 100)) < 1e-8;

export const ListadoCostos: React.FC<ListadoCostosProps> = ({
  costos,
  onActualizarCosto,
  onEliminarCosto,
  isLoading = false,
  canEdit = true,
  canDelete = true,
  modoEdicion = 'confirmacion',
  onReordenar,
  mensajeVacio = 'No hay items registrados aún',
  onEditarCalculo,
  bases = BASES_CALCULO_VACIAS,
  cotizacionDolar = null,
  destinosPago,
}) => {
  const esModoAuto = modoEdicion === 'auto';
  const [costoAEliminar, setCostoAEliminar] = useState<Costo | null>(null);
  const [edicionActiva, setEdicionActiva] = useState<EdicionActiva | null>(null);
  const [campoGuardando, setCampoGuardando] = useState<string | null>(null);
  const guardadoEnCursoRef = useRef(false);
  const montoInputRef = useRef<NumberInputRef>(null);
  const importeRealInputRef = useRef<NumberInputRef>(null);
  const bonificacionInputRef = useRef<NumberInputRef>(null);
  const nombreInputRef = useRef<HTMLInputElement>(null);
  // El popover de Radix vive en un portal fuera de la fila: mientras está abierto
  // el focusout no debe interpretarse como "salí de la fila".
  const selectAbiertoRef = useRef(false);
  // Evita que el focusout provocado por desmontar el input tras Escape guarde.
  const descartandoRef = useRef(false);

  const handleConfirmarEliminar = () => {
    if (costoAEliminar) {
      onEliminarCosto(costoAEliminar.id);
      setCostoAEliminar(null);
    }
  };

  const iniciarEdicion = (costo: Costo, campo: CampoEditable) => {
    if (
      isLoading ||
      campoGuardando ||
      (edicionActiva && edicionActiva.costoId !== costo.id)
    ) {
      return;
    }

    setEdicionActiva((actual) => {
      if (actual?.costoId === costo.id) {
        return {
          ...actual,
          campos: actual.campos.includes(campo)
            ? actual.campos
            : [...actual.campos, campo],
        };
      }

      return {
        costoId: costo.id,
        campos: [campo],
        valores: valoresIniciales(costo),
      };
    });
  };

  const seleccionarValor = (
    costo: Costo,
    campo: 'tipo' | 'moneda',
    valor: TipoItem | TipoMoneda
  ) => {
    if (edicionActiva && edicionActiva.costoId !== costo.id) return;

    const base: EdicionActiva =
      edicionActiva?.costoId === costo.id
        ? edicionActiva
        : {
          costoId: costo.id,
          campos: [] as CampoEditable[],
          valores: valoresIniciales(costo),
        };

    const siguiente: EdicionActiva = {
      ...base,
      campos: base.campos.includes(campo)
        ? base.campos
        : [...base.campos, campo],
      valores: {
        ...base.valores,
        [campo]: valor,
      },
    };

    setEdicionActiva(siguiente);

    // En modo auto el select guarda al elegir, arrastrando los campos pendientes
    // de la misma fila en una única llamada.
    if (esModoAuto) {
      void guardarEdicion(costo, siguiente);
    }
  };

  const cancelarEdicion = () => {
    if (!guardadoEnCursoRef.current) {
      descartandoRef.current = true;
      setEdicionActiva(null);
      setTimeout(() => {
        descartandoRef.current = false;
      }, 0);
    }
  };

  // En modo auto no cerramos la edición ante un dato inválido: devolvemos el foco
  // al campo culpable para no perder los cambios válidos de la fila.
  const rechazarPorValidacion = (
    mensaje: string,
    campo: 'nombre' | 'monto' | 'importeReal' | 'bonificacion'
  ) => {
    toast.error(mensaje);

    if (esModoAuto) {
      setTimeout(() => {
        if (campo === 'nombre') {
          nombreInputRef.current?.focus();
        } else if (campo === 'importeReal') {
          importeRealInputRef.current?.focus();
        } else if (campo === 'bonificacion') {
          bonificacionInputRef.current?.focus();
        } else {
          montoInputRef.current?.focus();
        }
      }, 0);
    }
  };

  const guardarEdicion = async (
    costo: Costo,
    edicion: EdicionActiva | null = edicionActiva
  ) => {
    if (
      !edicion ||
      edicion.costoId !== costo.id ||
      guardadoEnCursoRef.current
    ) {
      return;
    }

    const data: Partial<CostoFormData> = {};

    if (edicion.campos.includes('nombre')) {
      const nombre = edicion.valores.nombre.trim();

      if (!nombre) {
        rechazarPorValidacion('El nombre del item es obligatorio', 'nombre');
        return;
      }

      if (nombre.length > 255) {
        rechazarPorValidacion(
          'El nombre no puede exceder 255 caracteres',
          'nombre'
        );
        return;
      }

      if (nombre !== costo.nombre) {
        data.nombre = nombre;
      }
    }

    if (edicion.campos.includes('monto')) {
      const monto = montoInputRef.current?.getValue();

      // Los items predeterminados nacen en 0, así que el cero es válido.
      if (
        monto === null ||
        monto === undefined ||
        monto < 0 ||
        monto > MONTO_MAXIMO ||
        !tieneHastaDosDecimales(monto)
      ) {
        rechazarPorValidacion(
          'El monto no puede ser negativo y admite hasta 2 decimales',
          'monto'
        );
        return;
      }

      // Se edita el bruto: el backend vuelve a aplicar la bonificación.
      if (monto !== brutoDe(costo)) {
        data.monto = monto;
      }
    }

    if (edicion.campos.includes('bonificacion')) {
      const vacio = (bonificacionInputRef.current?.getRawValue() ?? edicion.valores.bonificacionValor).trim() === '';

      if (vacio) {
        // Vaciar el valor quita la bonificación.
        if (costo.bonificacionTipo) {
          data.bonificacion_tipo = null;
          data.bonificacion_valor = null;
        }
      } else {
        const resultado = armarBonificacion(
          edicion.valores.bonificacionTipo,
          bonificacionInputRef.current?.getValue() ?? parseFloat(edicion.valores.bonificacionValor)
        );

        if ('error' in resultado) {
          rechazarPorValidacion(resultado.error, 'bonificacion');
          return;
        }

        if (
          resultado.datos.bonificacion_tipo !== (costo.bonificacionTipo ?? null) ||
          resultado.datos.bonificacion_valor !== (costo.bonificacionValor ?? null)
        ) {
          Object.assign(data, resultado.datos);
        }
      }
    }

    // Si en la misma edición el ítem pasa a ingreso, el backend descarta el
    // importe real: no tiene sentido enviarlo.
    const tipoFinal = edicion.campos.includes('tipo')
      ? edicion.valores.tipo
      : costo.tipo;

    if (edicion.campos.includes('importeReal') && tipoFinal === 'egreso') {
      const vacio = (importeRealInputRef.current?.getRawValue() ?? '').trim() === '';
      const importeReal = vacio ? null : importeRealInputRef.current?.getValue();

      if (
        importeReal !== null &&
        (importeReal === undefined ||
          importeReal < 0 ||
          importeReal > MONTO_MAXIMO ||
          !tieneHastaDosDecimales(importeReal))
      ) {
        rechazarPorValidacion(
          'El importe real no puede ser negativo y admite hasta 2 decimales',
          'importeReal'
        );
        return;
      }

      // Vacío = sin importe real: el costo real vuelve a ser el monto.
      if (importeReal !== (costo.importeReal ?? null)) {
        data.importe_real = importeReal;
      }
    }

    if (
      edicion.campos.includes('tipo') &&
      edicion.valores.tipo !== costo.tipo
    ) {
      data.tipo = edicion.valores.tipo;
    }

    if (
      edicion.campos.includes('moneda') &&
      edicion.valores.moneda !== costo.moneda
    ) {
      data.moneda = edicion.valores.moneda;
    }

    if (Object.keys(data).length === 0) {
      setEdicionActiva(null);
      return;
    }

    guardadoEnCursoRef.current = true;
    setCampoGuardando(costo.id);

    try {
      await onActualizarCosto(costo.id, data);
      setEdicionActiva(null);
    } catch {
      toast.error('Error al actualizar el item. Intente nuevamente.');
      setEdicionActiva(null);
    } finally {
      guardadoEnCursoRef.current = false;
      setCampoGuardando(null);
    }
  };

  // Modo auto: la sesión de edición es la fila completa. Moverse entre campos de
  // la misma fila no guarda; salir de la fila manda un único PUT con todo lo tocado.
  const handleFocusOutFila =
    (costo: Costo) => (event: React.FocusEvent<HTMLDivElement>) => {
      if (!esModoAuto) return;
      if (edicionActiva?.costoId !== costo.id) return;
      if (selectAbiertoRef.current || descartandoRef.current) return;
      if (campoGuardando) return;
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
        return;
      }

      void guardarEdicion(costo);
    };

  const cambiarDestino = async (costo: Costo, destinoPagoId: string) => {
    if (destinoPagoId === costo.destinoPagoId) return;

    setCampoGuardando(costo.id);
    try {
      await onActualizarCosto(costo.id, { destino_pago_id: destinoPagoId });
    } catch {
      toast.error('No se pudo cambiar el destino de pago.');
    } finally {
      setCampoGuardando(null);
    }
  };

  /** null vuelve a imprimir el ítem en su propia moneda. */
  const cambiarMonedaImpresion = async (costo: Costo, monedaImpresion: TipoMoneda | null) => {
    setCampoGuardando(costo.id);
    try {
      await onActualizarCosto(costo.id, { moneda_impresion: monedaImpresion });
    } catch {
      toast.error('No se pudo cambiar la moneda de impresión.');
    } finally {
      setCampoGuardando(null);
    }
  };

  const handleOpenChangeSelect = (open: boolean) => {
    if (open) {
      selectAbiertoRef.current = true;
      return;
    }

    // Al cerrar, Radix devuelve el foco al trigger un tick después.
    setTimeout(() => {
      selectAbiertoRef.current = false;
    }, 0);
  };

  const handleKeyDownCampo = (
    costo: Costo,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelarEdicion();
      return;
    }

    if (event.key === 'Enter' && esModoAuto) {
      event.preventDefault();
      void guardarEdicion(costo);
    }
  };

  // Orden de impresión, igual que el backend: por `orden` y, a igual orden, por
  // fecha de creación.
  const costosOrdenados = [...costos].sort(
    (a, b) =>
      (a.orden ?? 0) - (b.orden ?? 0) ||
      new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime()
  );

  const puedeReordenar = !!onReordenar && canEdit;

  const reordenar = async (ids: string[]) => {
    try {
      await onReordenar?.(ids);
    } catch {
      toast.error('No se pudieron reordenar los ítems.');
    }
  };

  if (costos.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <svg
          className="mx-auto h-12 w-12 text-muted-foreground/50"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <p className="mt-2 text-sm">{mensajeVacio}</p>
      </div>
    );
  }

  return (
    <>
      {/* Sin permiso para reordenar, las filas se muestran sin manija. */}
      <ListaReordenable
        items={costosOrdenados}
        onReordenar={(ids) => void reordenar(ids)}
        etiquetaItem={(costo) => costo.nombre}
        deshabilitado={!puedeReordenar || isLoading || !!campoGuardando || !!edicionActiva}
        className="space-y-2"
      >
        {(costo, asa) => (
          <div
            key={costo.id}
            onBlur={handleFocusOutFila(costo)}
            className="flex flex-wrap items-center justify-between gap-y-2 p-3 bg-card border rounded-lg hover:bg-accent/50 transition-colors"
          >
            {puedeReordenar && <div className="-ml-1 mr-2">{asa}</div>}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {edicionActiva?.costoId === costo.id &&
                  edicionActiva.campos.includes('nombre') ? (
                  <Input
                    ref={nombreInputRef}
                    autoFocus
                    aria-label={`Editar nombre de ${costo.nombre}`}
                    value={edicionActiva.valores.nombre}
                    maxLength={255}
                    onFocus={(event) => event.currentTarget.select()}
                    onChange={(event) =>
                      setEdicionActiva((actual) =>
                        actual
                          ? {
                            ...actual,
                            valores: {
                              ...actual.valores,
                              nombre: event.target.value,
                            },
                          }
                          : actual
                      )
                    }
                    onKeyDown={(event) => handleKeyDownCampo(costo, event)}
                    disabled={campoGuardando === costo.id}
                    className="h-8 min-w-0 max-w-sm flex-1 text-base font-semibold"
                  />
                ) : canEdit ? (
                  <button
                    type="button"
                    onClick={() => iniciarEdicion(costo, 'nombre')}
                    disabled={
                      isLoading ||
                      !!campoGuardando ||
                      (!!edicionActiva && edicionActiva.costoId !== costo.id)
                    }
                    className="min-w-0 truncate rounded text-left text-base font-semibold underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    title="Editar nombre"
                  >
                    {costo.nombre}
                  </button>
                ) : (
                  <span className="text-base font-semibold">{costo.nombre}</span>
                )}

                {canEdit ? (
                  <Select
                    value={
                      edicionActiva?.costoId === costo.id &&
                        edicionActiva.campos.includes('tipo')
                        ? edicionActiva.valores.tipo
                        : costo.tipo
                    }
                    onValueChange={(tipo: TipoItem) =>
                      seleccionarValor(costo, 'tipo', tipo)
                    }
                    onOpenChange={handleOpenChangeSelect}
                    disabled={
                      isLoading ||
                      !!campoGuardando ||
                      (!!edicionActiva && edicionActiva.costoId !== costo.id)
                    }
                  >
                    <SelectTrigger
                      size="sm"
                      aria-label={`Cambiar tipo de ${costo.nombre}`}
                      className={`h-6 min-w-20 shrink-0 border-0 px-2 py-0 text-xs font-normal shadow-none ${(edicionActiva?.costoId === costo.id &&
                        edicionActiva.campos.includes('tipo')
                        ? edicionActiva.valores.tipo
                        : costo.tipo) === 'ingreso'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-gray-100 text-gray-600'
                        }`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ingreso">Ingreso</SelectItem>
                      <SelectItem value="egreso">Egreso</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge
                    variant="secondary"
                    className={
                      costo.tipo === 'ingreso'
                        ? 'bg-blue-50 text-blue-700 font-normal'
                        : 'bg-gray-100 text-gray-600 font-normal'
                    }
                  >
                    {costo.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                  </Badge>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 text-sm text-muted-foreground">
                {edicionActiva?.costoId === costo.id &&
                  edicionActiva.campos.includes('monto') ? (
                  <NumberInput
                    ref={montoInputRef}
                    autoFocus
                    aria-label={`Editar monto de ${costo.nombre}`}
                    value={edicionActiva.valores.monto}
                    decimals={2}
                    onFocus={(event) => event.currentTarget.select()}
                    onChange={(valor) =>
                      setEdicionActiva((actual) =>
                        actual
                          ? {
                            ...actual,
                            valores: { ...actual.valores, monto: valor },
                          }
                          : actual
                      )
                    }
                    onKeyDown={(event) => handleKeyDownCampo(costo, event)}
                    disabled={campoGuardando === costo.id}
                    containerClassName="w-40"
                    className="h-8 font-semibold"
                  />
                ) : canEdit ? (
                  <button
                    type="button"
                    onClick={() => iniciarEdicion(costo, 'monto')}
                    disabled={
                      isLoading ||
                      !!campoGuardando ||
                      (!!edicionActiva && edicionActiva.costoId !== costo.id)
                    }
                    className={`rounded underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${costo.bonificacionTipo ? 'line-through' : 'font-semibold'}`}
                    title={
                      costo.modoCalculo === 'porcentaje'
                        ? 'Editar monto (deja de calcularse sobre la base imponible)'
                        : costo.bonificacionTipo
                          ? 'Editar monto sin bonificar'
                          : 'Editar monto'
                    }
                  >
                    {formatearMonto(brutoDe(costo), costo.moneda)}
                  </button>
                ) : (
                  <span className={costo.bonificacionTipo ? 'line-through' : 'font-semibold'}>
                    {formatearMonto(brutoDe(costo), costo.moneda)}
                  </span>
                )}
                {/* Bonificado: el bruto queda tachado y se destaca el neto que se cobra. */}
                {costo.bonificacionTipo && (
                  <span className="font-semibold text-foreground" title="Monto bonificado que se le cobra al cliente">
                    {formatearMonto(costo.monto, costo.moneda)}
                  </span>
                )}
                {/* Bonificación: porcentaje del monto o monto fijo. Vaciar el valor la quita. */}
                {edicionActiva?.costoId === costo.id &&
                  edicionActiva.campos.includes('bonificacion') ? (
                  <span className="flex items-center gap-1">
                    <Select
                      value={edicionActiva.valores.bonificacionTipo}
                      onValueChange={(tipo: TipoBonificacion) =>
                        setEdicionActiva((actual) =>
                          actual
                            ? {
                              ...actual,
                              valores: { ...actual.valores, bonificacionTipo: tipo },
                            }
                            : actual
                        )
                      }
                      onOpenChange={handleOpenChangeSelect}
                      disabled={campoGuardando === costo.id}
                    >
                      <SelectTrigger
                        size="sm"
                        aria-label={`Tipo de bonificación de ${costo.nombre}`}
                        className="h-8 w-16 px-2 text-xs"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="porcentaje">%</SelectItem>
                        <SelectItem value="monto">{costo.moneda === 'USD' ? 'US$' : '$'}</SelectItem>
                      </SelectContent>
                    </Select>
                    <NumberInput
                      ref={bonificacionInputRef}
                      autoFocus
                      aria-label={`Editar bonificación de ${costo.nombre}`}
                      value={edicionActiva.valores.bonificacionValor}
                      decimals={edicionActiva.valores.bonificacionTipo === 'porcentaje' ? 4 : 2}
                      placeholder="Sin bonificación"
                      onFocus={(event) => event.currentTarget.select()}
                      onChange={(valor) =>
                        setEdicionActiva((actual) =>
                          actual
                            ? {
                              ...actual,
                              valores: { ...actual.valores, bonificacionValor: valor },
                            }
                            : actual
                        )
                      }
                      onKeyDown={(event) => handleKeyDownCampo(costo, event)}
                      disabled={campoGuardando === costo.id}
                      containerClassName="w-36"
                      className="h-8"
                    />
                  </span>
                ) : costo.bonificacionTipo && costo.bonificacionValor != null ? (
                  canEdit ? (
                    <button
                      type="button"
                      onClick={() => iniciarEdicion(costo, 'bonificacion')}
                      disabled={
                        isLoading ||
                        !!campoGuardando ||
                        (!!edicionActiva && edicionActiva.costoId !== costo.id)
                      }
                      className="rounded text-emerald-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      title="Editar bonificación"
                    >
                      {describirBonificacion(costo.bonificacionTipo, costo.bonificacionValor, costo.moneda)}
                    </button>
                  ) : (
                    <Badge variant="secondary" className="bg-emerald-50 font-normal text-emerald-700">
                      {describirBonificacion(costo.bonificacionTipo, costo.bonificacionValor, costo.moneda)}
                    </Badge>
                  )
                ) : (
                  canEdit && (
                    <button
                      type="button"
                      onClick={() => iniciarEdicion(costo, 'bonificacion')}
                      disabled={
                        isLoading ||
                        !!campoGuardando ||
                        (!!edicionActiva && edicionActiva.costoId !== costo.id)
                      }
                      aria-label={`Agregar bonificación a ${costo.nombre}`}
                      className="rounded text-xs text-muted-foreground/80 underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      title="Bonificar un porcentaje o un monto fijo"
                    >
                      + Bonificación
                    </button>
                  )
                )}
                <span>•</span>
                {canEdit ? (
                  <Select
                    value={
                      edicionActiva?.costoId === costo.id &&
                        edicionActiva.campos.includes('moneda')
                        ? edicionActiva.valores.moneda
                        : costo.moneda
                    }
                    onValueChange={(moneda: TipoMoneda) =>
                      seleccionarValor(costo, 'moneda', moneda)
                    }
                    onOpenChange={handleOpenChangeSelect}
                    disabled={
                      isLoading ||
                      !!campoGuardando ||
                      (!!edicionActiva && edicionActiva.costoId !== costo.id)
                    }
                  >
                    <SelectTrigger
                      size="sm"
                      aria-label={`Cambiar moneda de ${costo.nombre}`}
                      className="h-6 border-0 px-2 py-0 text-xs font-semibold shadow-none"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ARS">ARS</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span>{costo.moneda}</span>
                )}
                {/* Moneda de impresión: en qué moneda sale el ítem en el PDF. */}
                <MonedaImpresion
                  costo={costo}
                  cotizacionDolar={cotizacionDolar}
                  canEdit={canEdit}
                  disabled={
                    isLoading ||
                    !!campoGuardando ||
                    (!!edicionActiva && edicionActiva.costoId !== costo.id)
                  }
                  onOpenChange={handleOpenChangeSelect}
                  onCambiar={(monedaImpresion) => void cambiarMonedaImpresion(costo, monedaImpresion)}
                />
                {destinosPago && (
                  <>
                    <span>•</span>
                    {canEdit ? (
                      <SelectorDestinoPago
                        compacto
                        value={costo.destinoPagoId}
                        opciones={destinosPago}
                        onChange={(destinoPagoId) => void cambiarDestino(costo, destinoPagoId)}
                        onOpenChange={handleOpenChangeSelect}
                        ariaLabel={`Destino de pago de ${costo.nombre}`}
                        disabled={
                          isLoading ||
                          !!campoGuardando ||
                          (!!edicionActiva && edicionActiva.costoId !== costo.id)
                        }
                      />
                    ) : costo.destinoPagoId ? (
                      <span>
                        {destinosPago.find((destino) => destino.id === costo.destinoPagoId)?.nombre}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-amber-700"
                        title={TEXTO_SIN_DESTINO}
                      >
                        <AlertCircle className="h-3.5 w-3.5" />
                        Sin destino
                      </span>
                    )}
                  </>
                )}
                {/* Importe real: costo efectivo de un egreso para la escribanía. */}
                {costo.tipo === 'egreso' &&
                  (edicionActiva?.costoId === costo.id &&
                    edicionActiva.campos.includes('importeReal') ? (
                    <NumberInput
                      ref={importeRealInputRef}
                      autoFocus
                      aria-label={`Editar importe real de ${costo.nombre}`}
                      value={edicionActiva.valores.importeReal}
                      decimals={2}
                      placeholder="Igual al monto"
                      onFocus={(event) => event.currentTarget.select()}
                      onChange={(valor) =>
                        setEdicionActiva((actual) =>
                          actual
                            ? {
                              ...actual,
                              valores: { ...actual.valores, importeReal: valor },
                            }
                            : actual
                        )
                      }
                      onKeyDown={(event) => handleKeyDownCampo(costo, event)}
                      disabled={campoGuardando === costo.id}
                      containerClassName="w-40"
                      className="h-8"
                    />
                  ) : costo.importeReal !== null && costo.importeReal !== undefined ? (
                    canEdit ? (
                      <button
                        type="button"
                        onClick={() => iniciarEdicion(costo, 'importeReal')}
                        disabled={
                          isLoading ||
                          !!campoGuardando ||
                          (!!edicionActiva && edicionActiva.costoId !== costo.id)
                        }
                        className="rounded text-amber-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        title="Editar importe real (costo efectivo para la escribanía)"
                      >
                        Real: {formatearMonto(costo.importeReal, costo.moneda)}
                      </button>
                    ) : (
                      <span
                        className="text-amber-700"
                        title="Costo efectivo para la escribanía"
                      >
                        Real: {formatearMonto(costo.importeReal, costo.moneda)}
                      </span>
                    )
                  ) : (
                    canEdit && (
                      <button
                        type="button"
                        onClick={() => iniciarEdicion(costo, 'importeReal')}
                        disabled={
                          isLoading ||
                          !!campoGuardando ||
                          (!!edicionActiva && edicionActiva.costoId !== costo.id)
                        }
                        aria-label={`Agregar importe real a ${costo.nombre}`}
                        className="rounded text-xs text-muted-foreground/80 underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        title="Cargar lo que realmente le cuesta a la escribanía"
                      >
                        + Importe real
                      </button>
                    )
                  ))}
                {costo.modoCalculo === 'porcentaje' && (
                  <Badge
                    variant="secondary"
                    className="bg-violet-50 font-normal text-violet-700"
                    title="Se recalcula al cambiar la base imponible"
                  >
                    {describirCalculo(
                      costo.porcentaje,
                      costo.sumaFija,
                      costo.porcentajeAdicional,
                      costo.moneda,
                      costo.baseCalculo,
                      costo.sumaFijaMoneda
                    )}
                  </Badge>
                )}
                {faltaCotizacion(costo, bases, cotizacionDolar) && (
                  <span
                    className="inline-flex items-center gap-1 text-xs text-amber-700"
                    title="Lo que está en otra moneda no cuenta hasta que se cargue la cotización"
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Falta la cotización del dólar
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex w-full items-center justify-end gap-2 sm:ml-4 sm:w-auto">
              {!esModoAuto && edicionActiva?.costoId === costo.id ? (
                <>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => void guardarEdicion(costo)}
                    disabled={isLoading || !!campoGuardando}
                    title="Guardar cambios"
                    className="text-green-600 hover:bg-green-50 hover:text-green-700"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={cancelarEdicion}
                    disabled={isLoading || !!campoGuardando}
                    title="Cancelar edición"
                    className="text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : esModoAuto && campoGuardando === costo.id ? (
                <span
                  role="status"
                  title="Guardando cambios"
                  className="flex h-9 w-9 items-center justify-center text-muted-foreground"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                </span>
              ) : (
                <>
                  {onEditarCalculo && canEdit && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onEditarCalculo(costo)}
                      disabled={isLoading || !!edicionActiva}
                      title="Calcular sobre la base imponible"
                      aria-label={`Editar cálculo de ${costo.nombre}`}
                      className="text-muted-foreground"
                    >
                      <Percent className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setCostoAEliminar(costo)}
                      disabled={isLoading}
                      title="Eliminar item"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </ListaReordenable>

      {/* Confirmation dialog */}
      <Dialog
        open={!!costoAEliminar}
        onOpenChange={() => setCostoAEliminar(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar eliminación</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              ¿Está seguro de eliminar el item{' '}
              <strong className="text-foreground">
                {costoAEliminar?.nombre}
              </strong>
              ?
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Esta acción no se puede deshacer.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCostoAEliminar(null)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmarEliminar}
              disabled={isLoading}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
